import {
  DestinationPlatform,
  VideoTypeConfig,
} from 'blocks/DestinationPlatforms';
import { RejectedReason } from 'components/FileUploader';
import { TemplateEditorStateExport } from 'components/VideoTemplateEditor';
import Wizard, { WizardProps } from 'components/Wizard';
import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import { VideoUploadArgs } from 'redux/modules/video-upload';
import { AspectRatioName } from 'types';
import { ApplicationError } from 'utils/ApplicationError';
import { WizardFlow } from '../../types';
import { VideoWizardObjective } from '../types';
import { VideoFlowAction, VideoFlowState } from './state';

export enum VideoFlowStep {
  OBJECTIVE = 'objective',
  ASPECT_RATIO = 'aspectRatio',
  UPLOAD_VIDEO = 'uploadVideo',
  DESTINATION = 'destination',
  CLIP_VIDEO = 'clipVideo',
  CUSTOMIZE = 'template',
  EXPORT = 'export',
  PROGRESS = 'progress',
}

export type VideoFlowErrorHandler = (error: Error, bail?: boolean) => void;

export type AspectRatioConfig =
  | { isAspectRatioLocked: true; defaultAspectRatio: AspectRatioName }
  | { isAspectRatioLocked?: false; defaultAspectRatio?: AspectRatioName };

export type VideoFlowProps = AspectRatioConfig & {
  defaultLanguage?: string;
  defaultObjective?: VideoWizardObjective;
  onError: VideoFlowErrorHandler;
  onTemplateSelect: (templateId?: string) => void;
  wizardRef: React.MutableRefObject<Wizard<VideoFlowStep>>;
  isTemplateLocked?: boolean;
  skipTemplateSelection?: boolean;
  stepId: VideoFlowStep;
  templateId?: string;
};

export type UseVideoFlow = (props: VideoFlowProps) => WizardFlow<VideoFlowStep>;

export type DestinationState = {
  platform: DestinationPlatform | undefined;
  videoType: VideoTypeConfig | undefined;
  useYoutubeMostReplayed: boolean | undefined;
};

export type VideoFlowInternalErrorHandler = (
  error: ApplicationError,
  file?: File,
  reason?: RejectedReason,
) => void;

export type VideoSubFlow = Pick<
  WizardProps<VideoFlowStep>,
  'bodyClassName' | 'buttonClassName' | 'steps' | 'onStepClick' | 'onStepChange'
> & {
  onSubmit?: () => void;
  rootStepCompletedName?: string;
};

export type VideoSubFlowProps = {
  onError: VideoFlowInternalErrorHandler;
  rootStepId: VideoFlowStep;
  stepId: VideoFlowStep;
  state: VideoFlowState;
  stateDispatch: React.Dispatch<VideoFlowAction>;
  wizardRef: React.RefObject<Wizard<VideoFlowStep>>;
};

export type UseVideoSubFlow = (props: VideoSubFlowProps) => VideoSubFlow;

export type VideoFlowSubmitContext = {
  aspectRatioName: AspectRatioName;
  clipEndMillis: number;
  clipStartMillis: number;
  isFullEpisode: boolean;
  language: string;
  transcribe: boolean;
  objective: VideoWizardObjective;
  platform?: DestinationPlatform;
  videoType?: VideoTypeConfig;
  isAutoFrame?: boolean;
  mediaImportedArgs: MediaImportedArgs;
  projectName: string;
  suggestedClipDurationMillis: number;
  useYoutubeMostReplayed?: boolean;
  videoDurationMillis: number;
  videoFile?: File;
  videoSrc?: string;
};

export type VideoFlowSubmitConfig = VideoFlowSubmitContext & {
  exportState: TemplateEditorStateExport;
};

export type VideoFlowUploadArgs = VideoUploadArgs & {
  waitForUpload?: boolean;
};
